// Phase 10B — Tickets page restyled in the AutoMind 2050 design language.
// Logic, API calls, and data flow are preserved verbatim — only the JSX +
// classNames change. No mock data fallbacks.

import React, { useState, useEffect } from 'react';
import { Ticket as TicketIcon, Plus } from 'lucide-react';
import { PageHeader } from '../components/ui/page-header';
import { DataCard } from '../components/ui/data-card';
import { SubTabBar } from '../components/ui/sub-tab-bar';
import { StatusPill } from '../components/ui/status-pill';
import { EmptyState } from '../components/ui/empty-state';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';

// Phase 10B — map ticket priority/status to the StatusPill tones.
const priorityTone = (p) => {
  switch (p) {
    case 'urgent':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
    default:
      return 'neutral';
  }
};

const statusTone = (s) => {
  switch (s) {
    case 'open':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
    default:
      return 'neutral';
  }
};

// Redact phone numbers to ***<last4> per the design rules.
const redactPhone = (phone) => {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  useEffect(() => {
    fetchTickets();

    // Auto-refresh every 30 seconds
    const intervalId = setInterval(() => {
      fetchTickets(false);
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // Filter tickets
    let filtered = tickets;

    if (statusFilter !== 'all') {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (t) =>
          t.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.customer_phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.subject?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTickets(filtered);
  }, [searchQuery, statusFilter, tickets]);

  const fetchTickets = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tickets`
      );
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setTickets(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setLoadError(true);
      if (showLoading) {
        toast({
          title: 'Error',
          description: 'Failed to fetch tickets',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    try {
      if (!formData.customer_name || !formData.customer_phone || !formData.subject) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive',
        });
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tickets`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) throw new Error('Failed to create ticket');

      toast({
        title: 'Ticket Created',
        description: `Ticket #${(await response.json()).id.slice(0, 8)} created`,
        duration: 3000,
      });

      setShowCreateDialog(false);
      resetForm();
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: 'Error',
        description: 'Failed to create ticket',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${ticketId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) throw new Error('Failed to update ticket');

      toast({
        title: 'Status Updated',
        description: `Ticket marked as ${newStatus}`,
        duration: 2000,
      });

      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast({
        title: 'Error',
        description: 'Failed to update ticket',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      subject: '',
      description: '',
      category: 'general',
      priority: 'medium',
    });
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Operations"
        title="Tickets"
        description="Customer support requests across every channel."
        actions={
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <button type="button" className="btn-2050 btn-primary">
                <Plus className="w-4 h-4" />
                New ticket
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create support ticket</DialogTitle>
                <DialogDescription>
                  Create a new customer support ticket
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Customer name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) =>
                      setFormData({ ...formData, customer_name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_phone">Phone *</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) =>
                      setFormData({ ...formData, customer_phone: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) =>
                        setFormData({ ...formData, category: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="appointment">Appointment</SelectItem>
                        <SelectItem value="complaint">Complaint</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) =>
                        setFormData({ ...formData, priority: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTicket}>Create ticket</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <SubTabBar
          value={statusFilter}
          onChange={setStatusFilter}
          items={[
            { id: 'all', label: 'All' },
            { id: 'open', label: 'Open' },
            { id: 'in_progress', label: 'In progress' },
            { id: 'resolved', label: 'Resolved' },
            { id: 'closed', label: 'Closed' },
          ]}
        />
        <div className="relative w-full sm:w-72">
          <Input
            placeholder="Search tickets…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-6">
        <DataCard
          title="Tickets"
          description={
            loading
              ? 'Loading…'
              : `${filteredTickets.length} of ${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`
          }
          loading={loading && tickets.length === 0}
          empty={!loading && (loadError || filteredTickets.length === 0)}
          emptyTitle={loadError ? 'No data yet' : 'No tickets found'}
          emptyDescription={
            loadError
              ? 'Unable to load right now.'
              : 'Try adjusting your filters or create a new ticket.'
          }
          emptyIcon={TicketIcon}
          bodyClassName={
            !loading && (loadError || filteredTickets.length === 0) ? 'p-6' : 'p-0'
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40">
                <tr>
                  <th className="px-6 py-3 text-left font-medium">Subject</th>
                  <th className="px-6 py-3 text-left font-medium">Customer</th>
                  <th className="px-6 py-3 text-left font-medium">Phone</th>
                  <th className="px-6 py-3 text-left font-medium">Priority</th>
                  <th className="px-6 py-3 text-left font-medium">Status</th>
                  <th className="px-6 py-3 text-left font-medium">Created</th>
                  <th className="px-6 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
                {filteredTickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="row-2050 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 transition-colors"
                  >
                    <td className="px-6 py-3">
                      <div className="font-medium text-ink-900 dark:text-ink-50 truncate max-w-xs">
                        {ticket.subject}
                      </div>
                      {ticket.description && (
                        <div className="text-xs text-ink-500 truncate max-w-xs mt-0.5">
                          {ticket.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-ink-700 dark:text-ink-200">
                      {ticket.customer_name || '—'}
                    </td>
                    <td className="px-6 py-3 nums text-ink-500">
                      {redactPhone(ticket.customer_phone)}
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill
                        tone={priorityTone(ticket.priority)}
                        label={ticket.priority || 'medium'}
                      />
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill
                        tone={statusTone(ticket.status)}
                        dot
                        label={(ticket.status || 'open').replace('_', ' ')}
                      />
                    </td>
                    <td className="px-6 py-3 nums text-ink-500">
                      {formatDate(ticket.created_at)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        {ticket.status === 'open' && (
                          <button
                            type="button"
                            className="btn-2050 btn-ghost"
                            onClick={() =>
                              handleUpdateStatus(ticket.id, 'in_progress')
                            }
                          >
                            Start
                          </button>
                        )}
                        {(ticket.status === 'open' ||
                          ticket.status === 'in_progress') && (
                          <button
                            type="button"
                            className="btn-2050 btn-ghost"
                            onClick={() =>
                              handleUpdateStatus(ticket.id, 'resolved')
                            }
                          >
                            Resolve
                          </button>
                        )}
                        {ticket.status === 'resolved' && (
                          <button
                            type="button"
                            className="btn-2050 btn-ghost"
                            onClick={() =>
                              handleUpdateStatus(ticket.id, 'closed')
                            }
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataCard>
      </div>
    </div>
  );
};

export default Tickets;
